import { CSSResultGroup } from '@lit/reactive-element/css-tag';

import { html, HTMLTemplateResult, LitElement, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { UmRipple } from '../ripple/ripple.js';
import { styles as baseStyles } from './base.styles.js';
import { styles } from './button-wrapper.styles.js';
import { redispatchEvent } from './events/redispatch-event.js';

import '../elevation/elevation.js';
import '../ripple/ripple.js';

export abstract class UmButtonWrapper extends LitElement {
  static override styles: CSSResultGroup = [baseStyles, styles];

  /**
   * Whether the button is disabled or not.
   */
  @property({ type: Boolean, reflect: true }) disabled = false;

  @state() renderRipple = true;

  /**
   * The URL that the link button points to.
   */
  @property() href: string | undefined;

  /**
   * Where to display the linked `href` URL for a link button. Common options
   * include `_blank` to open in a new tab.
   */
  @property() target: string | undefined;

  @property() name: string | undefined;

  @query('.button') readonly buttonElement!: HTMLElement;
  @query('u-ripple') private readonly _ripple!: UmRipple;

  protected innerRole: string | null = null;

  get pathname(): string {
    return (this.buttonElement as HTMLAnchorElement)?.pathname;
  }

  protected override render(): HTMLTemplateResult {

    const contents = typeof this.href === 'string'
      ? this.#renderLink()
      : this.#renderButton();

    const containerClasses = classMap(this._getContainerClasses());

    return html`<div class="container ${containerClasses}" part="container"> ${contents}</div>`;
  }

  protected _getContainerClasses(): Record<string, boolean> {
    return { disabled: this.disabled };
  }

  #renderButton() {
    return html`
      <div class="content">${this._renderContent()}</div>
      <button
        id="button"
        class="button focus-ring"
        ?disabled=${this.disabled}
        aria-label=${this.getAriaLabel() || nothing}
        aria-labelledby="${this.getAriaLabel() ? nothing : 'text'}"
        .role=${this.innerRole}
        type="button"
        @click=${this.#innerClickHandler}>
        <u-ripple ?disabled=${this.disabled || !this.renderRipple}></u-ripple>
        <u-elevation></u-elevation>
      </button>
    `;
  }

  #renderLink() {
    return html`
      <div class="content">${this._renderContent()}</div>
      <a
        id="link"
        class="button"
        href=${this.disabled ? nothing : this.href}
        aria-disabled=${this.disabled || nothing}
        aria-label=${this.ariaLabel || nothing}
        aria-labelledby="${this.ariaLabel ? nothing : 'text'}"
        .role=${this.innerRole}
        target=${this.target || nothing}
        @click=${this.#innerClickHandler}>
        <u-elevation></u-elevation>
        <u-ripple ?disabled=${this.disabled || !this.renderRipple}></u-ripple>
      </a>
    `;
  }

  protected abstract _renderContent(): HTMLTemplateResult;

  override connectedCallback() {
    super.connectedCallback();

    this.addEventListener('focus', this.#innerFocusHandler);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('focus', this.#innerFocusHandler);
  }

  override focus() {
    this.buttonElement?.focus();
  }

  override blur() {
    this.buttonElement?.blur();
  }

  protected getAriaLabel(): string | null {
    return this.ariaLabel;
  }

  #innerFocusHandler(): void {
    const tabIndexAttributeValue = this.getAttribute('tabindex');

    if (tabIndexAttributeValue !== '0') {
      return;
    }

    this.removeAttribute('tabindex');
    setTimeout(() => this.buttonElement?.focus());
  }

  #innerClickHandler(event: MouseEvent): void {
    if (this.disabled) {
      return;
    }

    const preventDefault = !redispatchEvent(this, event);

    if (preventDefault) {
      return;
    }

    if (!(event as PointerEvent).pointerType) {
      this._ripple.createRipple();
    }

    this._handleClick(event);
  }

  protected _handleClick(_: UIEvent): void {
  }
}
